/**
 * Expense Tracker PWA - Form UI Module
 * Handles form interactions, config rendering, and expense submission
 * 
 * Migrated from public/app.js to use modular architecture:
 * - IndexedDB via db.js (replaces localStorage queue)
 * - appState for reactive state management
 * - sync.js for queue processing
 * - api.js for HTTP requests
 */

import { 
  addExpense, 
  getPendingCount, 
  getPendingSyncItems, 
  getCachedConfig, 
  cacheConfig 
} from './db.js';
import { appState } from './state.js';
import { processSyncQueue, isSyncing } from './sync.js';
import { api } from './api.js';

/** UI Version - increment on each deployment */
const APP_VERSION = '3.0.3';

/** 
 * Threshold (ms) for resetting datetime to "now" when app resumes from background.
 * If the app was hidden for longer than this, datetime resets on visibility restore.
 */
const DATETIME_RESET_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes

const App = {
  // Database reference (set by initApp)
  db: null,

  // UI State
  config: null,
  selectedCategory: null,
  selectedPayment: null,
  selectedType: null,
  selectedForWhom: 'Personal',  // Default to Personal

  // Timestamp when app was last visible (for iOS PWA resume detection)
  lastVisibleTimestamp: Date.now(),

  // DOM elements cache
  elements: {},

  /**
   * Initialize the application
   */
  async init() {
    this.cacheElements();
    this.setupEventListeners();
    this.setupStateSubscriptions();
    this.setupVisibilityHandler();
    this.displayVersion();

    // Check authentication status
    await this.checkAuth();
  },

  /**
   * Display UI version
   */
  displayVersion() {
    if (this.elements.versionDisplay) {
      this.elements.versionDisplay.textContent = `v${APP_VERSION}`;
    }
  },

  /**
   * Cache DOM elements
   */
  cacheElements() {
    this.elements = {
      loadingScreen: document.getElementById('loading-screen'),
      loginScreen: document.getElementById('login-screen'),
      mainScreen: document.getElementById('main-screen'),
      expenseDatetime: document.getElementById('expense-datetime'),
      nowBtn: document.getElementById('now-btn'),
      amount: document.getElementById('amount'),
      currency: document.getElementById('currency'),
      rateSection: document.getElementById('rate-section'),
      exchangeRate: document.getElementById('exchange-rate'),
      rateSource: document.getElementById('rate-source'),
      rmPreview: document.getElementById('rm-preview'),
      rmAmount: document.getElementById('rm-amount'),
      categories: document.getElementById('categories'),
      subcategory: document.getElementById('subcategory'),
      subcategorySection: document.getElementById('subcategory-section'),
      payments: document.getElementById('payments'),
      types: document.getElementById('types'),
      forWhom: document.getElementById('for-whom'),
      trip: document.getElementById('trip'),
      tripSection: document.getElementById('trip-section'),
      note: document.getElementById('note'),
      noteDropdown: document.getElementById('note-dropdown'),
      submitBtn: document.getElementById('submit-btn'),
      syncStatus: document.getElementById('sync-status'),
      queueIndicator: document.getElementById('queue-indicator'),
      queueCount: document.getElementById('queue-count'),
      toast: document.getElementById('toast'),
      versionDisplay: document.getElementById('version-display'),
      pendingModal: document.getElementById('pending-modal'),
      pendingList: document.getElementById('pending-list'),
      pendingModalClose: document.getElementById('pending-modal-close'),
      syncNowBtn: document.getElementById('sync-now-btn'),
      authModal: document.getElementById('auth-modal'),
      authModalClose: document.getElementById('auth-modal-close'),
      loginBtn: document.getElementById('login-btn')
    };
  },

  /**
   * Set up event listeners
   */
  setupEventListeners() {
    this.elements.submitBtn.addEventListener('click', () => this.submitExpense());
    this.elements.amount.addEventListener('input', () => {
      this.validateForm();
      this.updateRmPreview();
    });
    this.elements.note.addEventListener('keypress', (e) => {
      if (e.key === 'Enter') this.submitExpense();
    });
    this.elements.nowBtn.addEventListener('click', () => this.setDatetimeToNow());

    // For Whom button handlers (static buttons in HTML)
    this.elements.forWhom.querySelectorAll('button').forEach(btn => {
      btn.addEventListener('click', () => this.selectForWhom(btn));
    });

    // Note dropdown handlers
    this.elements.note.addEventListener('focus', () => this.showNoteDropdown());
    this.elements.note.addEventListener('input', () => this.filterNoteDropdown());
    // Close dropdown when clicking outside
    document.addEventListener('click', (e) => {
      if (!this.elements.note.contains(e.target) && !this.elements.noteDropdown.contains(e.target)) {
        this.hideNoteDropdown();
      }
    });

    // Currency change handler
    this.elements.currency.addEventListener('change', () => this.onCurrencyChange());

    // Rate input handler
    this.elements.exchangeRate.addEventListener('input', () => this.updateRmPreview());

    // Queue indicator click - open pending modal
    this.elements.queueIndicator.addEventListener('click', () => this.openPendingModal());

    // Pending modal close button
    if (this.elements.pendingModalClose) {
      this.elements.pendingModalClose.addEventListener('click', () => this.closePendingModal());
    }

    // Sync now button
    if (this.elements.syncNowBtn) {
      this.elements.syncNowBtn.addEventListener('click', () => {
        this.closePendingModal();
        if (!isSyncing()) {
          processSyncQueue(this.db);
        }
      });
    }

    // Modal backdrop click to close
    if (this.elements.pendingModal) {
      this.elements.pendingModal.addEventListener('click', (e) => {
        if (e.target === this.elements.pendingModal) {
          this.closePendingModal();
        }
      });
    }

    // Auth modal close button
    if (this.elements.authModalClose) {
      this.elements.authModalClose.addEventListener('click', () => this.closeAuthModal());
    }

    // Login button
    if (this.elements.loginBtn) {
      this.elements.loginBtn.addEventListener('click', () => this.goToLogin());
    }

    // Auth modal backdrop click to close
    if (this.elements.authModal) {
      this.elements.authModal.addEventListener('click', (e) => {
        if (e.target === this.elements.authModal) {
          this.closeAuthModal();
        }
      });
    }

    // Scroll inputs into view when focused (for mobile keyboard)
    this.setupKeyboardHandling();
  },

  /**
   * Subscribe to appState changes for reactive UI updates
   */
  setupStateSubscriptions() {
    // Pending count updates queue indicator
    appState.subscribe('pendingCount', (count) => {
      this.updateQueueIndicator(count);
    });

    // Sync status updates header display
    appState.subscribe('syncStatus', (status) => {
      this.updateSyncStatusDisplay(status);
    });

    // Auth error triggers modal
    appState.subscribe('syncStatus', (status) => {
      if (status === 'auth-error') {
        this.showAuthError();
      }
    });

    // Online status can trigger UI feedback
    appState.subscribe('isOnline', (isOnline) => {
      if (!isOnline) {
        this.updateSyncStatusDisplay('offline');
      }
    });
  },

  /**
   * Handle iOS PWA resume from background.
   * Resets datetime to "now" if app was hidden for longer than threshold.
   * This addresses the iOS PWA behaviour where the page is restored from memory
   * without a full reload, preserving stale datetime values.
   */
  setupVisibilityHandler() {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        // Record when app went to background
        this.lastVisibleTimestamp = Date.now();
      } else if (document.visibilityState === 'visible') {
        // Check elapsed time since last visible
        const elapsed = Date.now() - this.lastVisibleTimestamp;
        if (elapsed > DATETIME_RESET_THRESHOLD_MS) {
          console.log(`[App] Resuming after ${Math.round(elapsed / 1000)}s — resetting datetime to now`);
          this.setDatetimeToNow();
        }
        // Update timestamp for next cycle
        this.lastVisibleTimestamp = Date.now();
      }
    });
  },

  /**
   * Handle mobile keyboard - scroll focused element into view
   */
  setupKeyboardHandling() {
    const inputs = document.querySelectorAll('input, select, textarea');
    inputs.forEach(input => {
      input.addEventListener('focus', () => {
        // Delay to allow keyboard to appear
        setTimeout(() => {
          input.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 300);
      });
    });
  },

  /**
   * Check authentication status with server
   */
  async checkAuth() {
    try {
      const data = await api.get('/api/session');

      if (data.authenticated) {
        await this.onAuthenticated();
      } else {
        this.showLoginScreen();
      }
    } catch (error) {
      console.error('[App] Auth check failed:', error);
      // If offline and we have cached config, show main screen
      const cachedConfig = await getCachedConfig(this.db);
      if (cachedConfig) {
        this.config = cachedConfig;
        appState.set('config', cachedConfig);
        this.showMainScreen();
        this.renderConfig();
      } else {
        this.showLoginScreen();
      }
    }
  },

  /**
   * Handle successful authentication
   */
  async onAuthenticated() {
    this.showMainScreen();
    await this.loadConfig();
    
    // Initialize queue indicator from current pending count
    const count = await getPendingCount(this.db);
    this.updateQueueIndicator(count);

    // Trigger sync if online and items pending
    if (appState.get('isOnline') && count > 0 && !isSyncing()) {
      processSyncQueue(this.db);
    }
  },

  /**
   * Show loading screen
   */
  showLoadingScreen() {
    this.elements.loadingScreen.classList.remove('hidden');
    this.elements.loginScreen.classList.add('hidden');
    this.elements.mainScreen.classList.add('hidden');
  },

  /**
   * Show login screen
   */
  showLoginScreen() {
    this.elements.loadingScreen.classList.add('hidden');
    this.elements.loginScreen.classList.remove('hidden');
    this.elements.mainScreen.classList.add('hidden');
  },

  /**
   * Show main screen
   */
  showMainScreen() {
    this.elements.loadingScreen.classList.add('hidden');
    this.elements.loginScreen.classList.add('hidden');
    this.elements.mainScreen.classList.remove('hidden');
  },

  /**
   * Load config from server with IndexedDB caching
   */
  async loadConfig() {
    // Use IndexedDB cached config first
    const cached = await getCachedConfig(this.db);
    if (cached) {
      this.config = cached;
      appState.set('config', cached);
      this.renderConfig();
    }

    // Fetch fresh config if online
    if (appState.get('isOnline')) {
      try {
        const config = await api.get('/api/config');
        this.config = config;
        appState.set('config', config);
        await cacheConfig(this.db, config);
        this.renderConfig();
      } catch (error) {
        console.error('[App] Failed to fetch config:', error);
      }
    }
  },

  /**
   * Render config in UI
   */
  renderConfig() {
    if (!this.config) return;

    // Currencies
    if (this.config.currencies) {
      this.elements.currency.innerHTML = this.config.currencies
        .map(c => `<option value="${c}" ${c === 'MYR' ? 'selected' : ''}>${c}</option>`)
        .join('');
    }

    // Categories
    if (this.config.categories) {
      this.elements.categories.innerHTML = this.config.categories
        .map(cat => `<button type="button" data-value="${cat}">${cat}</button>`)
        .join('');

      this.elements.categories.querySelectorAll('button').forEach(btn => {
        btn.addEventListener('click', () => this.selectCategory(btn));
      });
    }

    // Payment methods
    if (this.config.paymentMethods) {
      this.elements.payments.innerHTML = this.config.paymentMethods
        .map(pay => `<button type="button" data-value="${pay}">${pay}</button>`)
        .join('');

      this.elements.payments.querySelectorAll('button').forEach(btn => {
        btn.addEventListener('click', () => this.selectPayment(btn));
      });
    }

    // Types
    if (this.config.types) {
      this.elements.types.innerHTML = this.config.types
        .map(type => `<button type="button" data-value="${type}">${type}</button>`)
        .join('');

      this.elements.types.querySelectorAll('button').forEach(btn => {
        btn.addEventListener('click', () => this.selectType(btn));
      });
    }

    // Trips (optional dropdown)
    if (this.config.trips && this.config.trips.length > 0) {
      this.elements.trip.innerHTML = '<option value="">None</option>' +
        this.config.trips.map(t => `<option value="${t}">${t}</option>`).join('');
      this.elements.tripSection.style.display = 'block';
    } else {
      this.elements.tripSection.style.display = 'block'; // Always show, even if empty
    }

    // Common notes (dropdown suggestions)
    this.renderNoteDropdown();

    // Set datetime default to now
    this.setDatetimeToNow();
  },

  /**
   * Set datetime input to current time
   */
  setDatetimeToNow() {
    const now = new Date();
    // Format: YYYY-MM-DDTHH:MM (datetime-local format)
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    this.elements.expenseDatetime.value = `${year}-${month}-${day}T${hours}:${minutes}`;
  },

  /**
   * Handle currency change - show/hide rate section and fetch rate
   */
  async onCurrencyChange() {
    const currency = this.elements.currency.value;
    const isForeign = currency !== 'MYR';

    // Show/hide rate section
    this.elements.rateSection.classList.toggle('hidden', !isForeign);

    if (isForeign) {
      // Fetch exchange rate
      const { rate, source } = await this.getExchangeRate(currency);
      if (rate) {
        this.elements.exchangeRate.value = rate.toFixed(4);
        this.elements.rateSource.textContent = source;
        this.elements.rateSource.className = `rate-source rate-${source}`;
      } else {
        this.elements.exchangeRate.value = '';
        this.elements.rateSource.textContent = 'manual';
        this.elements.rateSource.className = 'rate-source rate-manual';
      }
      this.updateRmPreview();
    } else {
      // MYR - reset rate
      this.elements.exchangeRate.value = '1';
    }
  },

  /**
   * Update RM preview based on amount and rate
   */
  updateRmPreview() {
    const amount = parseFloat(this.elements.amount.value) || 0;
    const rate = parseFloat(this.elements.exchangeRate.value) || 1;
    const rmAmount = amount * rate;
    this.elements.rmAmount.textContent = rmAmount.toFixed(2);
  },

  /**
   * Get exchange rate from cache or API
   * Uses localStorage for rate cache (separate from expense queue)
   */
  async getExchangeRate(currency) {
    const cacheKey = 'exchangeRates';
    const cached = JSON.parse(localStorage.getItem(cacheKey) || 'null');
    const isStale = cached && (Date.now() - cached.timestamp > 86400000); // 24h

    // Try cache first
    if (cached && !isStale && cached.rates && cached.rates[currency.toLowerCase()]) {
      return { rate: cached.rates[currency.toLowerCase()], source: 'cache' };
    }

    // Try API if online
    if (appState.get('isOnline')) {
      try {
        const currencies = this.config.currencies
          .filter(c => c !== 'MYR')
          .map(c => c.toLowerCase())
          .join(',');
        const data = await api.get('/api/rates', { currencies });
        localStorage.setItem(cacheKey, JSON.stringify({
          rates: data.rates,
          timestamp: Date.now()
        }));
        const rate = data.rates[currency.toLowerCase()];
        return { rate, source: 'api' };
      } catch (e) {
        console.warn('[App] Failed to fetch rates:', e);
      }
    }

    // Fallback to stale cache
    if (cached && cached.rates && cached.rates[currency.toLowerCase()]) {
      return { rate: cached.rates[currency.toLowerCase()], source: 'stale' };
    }

    // No rate available
    return { rate: null, source: 'manual' };
  },

  /**
   * Select category
   */
  selectCategory(btn) {
    this.elements.categories.querySelectorAll('button').forEach(b => b.classList.remove('selected'));
    btn.classList.add('selected');
    this.selectedCategory = btn.dataset.value;

    // Auto-fill type based on category defaults
    if (this.config.categoryDefaults && this.config.categoryDefaults[this.selectedCategory]) {
      const defaultType = this.config.categoryDefaults[this.selectedCategory];
      const typeBtn = this.elements.types.querySelector(`button[data-value="${defaultType}"]`);
      if (typeBtn) {
        this.selectType(typeBtn);
      }
    }

    // Show/hide subcategory dropdown based on config
    this.updateSubcategoryOptions();

    this.validateForm();
  },

  /**
   * Update subcategory dropdown based on selected category
   */
  updateSubcategoryOptions() {
    if (this.config.subcategories && this.config.subcategories[this.selectedCategory]) {
      const subs = this.config.subcategories[this.selectedCategory];
      this.elements.subcategory.innerHTML = '<option value="">None</option>' +
        subs.map(s => `<option value="${s}">${s}</option>`).join('');
      this.elements.subcategorySection.classList.remove('hidden');
    } else {
      this.elements.subcategory.value = '';
      this.elements.subcategorySection.classList.add('hidden');
    }
  },

  /**
   * Select payment
   */
  selectPayment(btn) {
    this.elements.payments.querySelectorAll('button').forEach(b => b.classList.remove('selected'));
    btn.classList.add('selected');
    this.selectedPayment = btn.dataset.value;
    this.validateForm();
  },

  /**
   * Select type
   */
  selectType(btn) {
    this.elements.types.querySelectorAll('button').forEach(b => b.classList.remove('selected'));
    btn.classList.add('selected');
    this.selectedType = btn.dataset.value;
    this.validateForm();
  },

  /**
   * Select for whom (Personal/Family)
   */
  selectForWhom(btn) {
    this.elements.forWhom.querySelectorAll('button').forEach(b => b.classList.remove('selected'));
    btn.classList.add('selected');
    this.selectedForWhom = btn.dataset.value;
  },

  /**
   * Render note dropdown with common notes from config
   */
  renderNoteDropdown() {
    if (!this.config?.commonNotes?.length) {
      this.elements.noteDropdown.innerHTML = '<div class="note-dropdown-empty">No suggestions configured</div>';
      return;
    }
    this.elements.noteDropdown.innerHTML = this.config.commonNotes
      .map(n => `<div class="note-dropdown-item" data-value="${this.escapeHtml(n)}">${this.escapeHtml(n)}</div>`)
      .join('');
    
    // Add click handlers to items
    this.elements.noteDropdown.querySelectorAll('.note-dropdown-item').forEach(item => {
      item.addEventListener('click', (e) => {
        e.stopPropagation();
        this.elements.note.value = item.dataset.value;
        this.hideNoteDropdown();
      });
    });
  },

  /**
   * Show note dropdown
   */
  showNoteDropdown() {
    if (!this.config?.commonNotes?.length) return;
    this.filterNoteDropdown();
    this.elements.noteDropdown.classList.remove('hidden');
  },

  /**
   * Hide note dropdown
   */
  hideNoteDropdown() {
    this.elements.noteDropdown.classList.add('hidden');
  },

  /**
   * Filter note dropdown based on current input
   */
  filterNoteDropdown() {
    const query = this.elements.note.value.toLowerCase().trim();
    const items = this.elements.noteDropdown.querySelectorAll('.note-dropdown-item');
    let hasVisible = false;

    items.forEach(item => {
      const matches = !query || item.dataset.value.toLowerCase().includes(query);
      item.style.display = matches ? '' : 'none';
      if (matches) hasVisible = true;
    });

    // Show empty state if no matches
    const emptyEl = this.elements.noteDropdown.querySelector('.note-dropdown-empty');
    if (!hasVisible && !emptyEl && this.config?.commonNotes?.length) {
      // All filtered out - hide dropdown
      this.hideNoteDropdown();
    }
  },

  /**
   * Escape HTML for safe rendering
   */
  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  },

  /**
   * Validate form
   */
  validateForm() {
    const amount = parseFloat(this.elements.amount.value);
    const isValid = amount > 0 && this.selectedCategory && this.selectedPayment && this.selectedType;
    this.elements.submitBtn.disabled = !isValid;
  },

  /**
   * Submit expense (offline-first via IndexedDB)
   */
  async submitExpense() {
    // Use selected datetime or fallback to now
    const datetimeValue = this.elements.expenseDatetime.value;
    const expenseDate = datetimeValue ? new Date(datetimeValue).toISOString() : new Date().toISOString();

    const expense = {
      date: expenseDate,
      amount: parseFloat(this.elements.amount.value),
      currency: this.elements.currency.value,
      rate: parseFloat(this.elements.exchangeRate.value) || 1,
      category: this.selectedCategory,
      subcategory: this.elements.subcategory.value || '',
      payment: this.selectedPayment,
      note: this.elements.note.value.trim(),
      type: this.selectedType,
      trip: this.elements.trip.value || '',
      forWhom: this.selectedForWhom || 'Personal'
    };

    this.elements.submitBtn.disabled = true;
    this.elements.submitBtn.textContent = 'Saving...';

    try {
      // Always queue to IndexedDB first (offline-first pattern)
      await addExpense(this.db, expense);

      // Update pending count in state
      const count = await getPendingCount(this.db);
      appState.set('pendingCount', count);

      // Format amount for confirmation message
      const formattedAmount = expense.currency === 'MYR'
        ? `RM ${expense.amount.toFixed(2)}`
        : `${expense.currency} ${expense.amount.toFixed(2)}`;
      this.showToast(`Added ${formattedAmount} · ${expense.category}`, 'success');
      this.resetForm();

      // Trigger sync if online
      if (appState.get('isOnline') && !isSyncing()) {
        processSyncQueue(this.db);
      }
    } catch (error) {
      console.error('[App] Failed to save expense:', error);
      this.showToast('Failed to save', 'error');
      this.elements.submitBtn.disabled = false;
      this.elements.submitBtn.textContent = 'Add Expense';
    }
  },

  /**
   * Update queue indicator visibility and count
   */
  updateQueueIndicator(count) {
    if (count > 0) {
      this.elements.queueIndicator.classList.remove('hidden');
      this.elements.queueCount.textContent = count;
    } else {
      this.elements.queueIndicator.classList.add('hidden');
    }
  },

  /**
   * Update sync status display in header
   */
  updateSyncStatusDisplay(status) {
    const statusMap = {
      'idle': { text: 'Synced', className: 'synced' },
      'syncing': { text: 'Syncing...', className: 'syncing' },
      'error': { text: 'Sync error', className: 'error' },
      'auth-error': { text: 'Auth error', className: 'error' },
      'offline': { text: 'Offline', className: '' }
    };
    const { text, className } = statusMap[status] || { text: '', className: '' };
    this.elements.syncStatus.textContent = text;
    this.elements.syncStatus.className = 'sync-status ' + className;
  },

  /**
   * Reset form to initial state
   */
  resetForm() {
    this.elements.amount.value = '';
    this.elements.note.value = '';
    this.elements.trip.value = '';
    this.elements.subcategory.value = '';
    this.elements.subcategorySection.classList.add('hidden');

    // Reset rate section
    this.elements.exchangeRate.value = '1';
    this.elements.rateSection.classList.add('hidden');
    this.elements.rmAmount.textContent = '0.00';
    this.elements.rateSource.textContent = '';

    // Reset currency to MYR
    this.elements.currency.value = 'MYR';

    this.selectedCategory = null;
    this.selectedPayment = null;
    this.selectedType = null;
    this.selectedForWhom = 'Personal';

    this.elements.categories.querySelectorAll('button').forEach(b => b.classList.remove('selected'));
    this.elements.payments.querySelectorAll('button').forEach(b => b.classList.remove('selected'));
    this.elements.types.querySelectorAll('button').forEach(b => b.classList.remove('selected'));
    
    // Reset forWhom to Personal (default)
    this.elements.forWhom.querySelectorAll('button').forEach(b => {
      b.classList.toggle('selected', b.dataset.value === 'Personal');
    });

    // Keep datetime as-is (useful for multiple backdated entries)

    this.elements.submitBtn.textContent = 'Add Expense';
    this.elements.submitBtn.disabled = true;
    this.elements.amount.focus();
  },

  /**
   * Show toast notification
   */
  showToast(message, type = '') {
    this.elements.toast.textContent = message;
    this.elements.toast.className = `toast ${type}`;
    this.elements.toast.onclick = null;
    this.elements.toast.style.cursor = 'default';

    setTimeout(() => this.elements.toast.classList.remove('hidden'), 10);
    setTimeout(() => this.elements.toast.classList.add('hidden'), 3000);
  },

  /**
   * Open pending queue modal
   */
  async openPendingModal() {
    if (!this.elements.pendingModal) return;

    await this.renderPendingList();
    this.elements.pendingModal.classList.remove('hidden');
  },

  /**
   * Close pending queue modal
   */
  closePendingModal() {
    if (!this.elements.pendingModal) return;

    this.elements.pendingModal.classList.add('hidden');
  },

  /**
   * Render pending expenses list in modal (from IndexedDB)
   */
  async renderPendingList() {
    if (!this.elements.pendingList) return;

    const queue = await getPendingSyncItems(this.db);

    if (queue.length === 0) {
      this.elements.pendingList.innerHTML = '<p class="pending-empty">No pending expenses</p>';
      return;
    }

    const html = queue.map((item) => {
      const expense = item.data;
      const date = new Date(expense.date);
      const dateStr = date.toLocaleDateString('en-MY', {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit'
      });

      return `
        <div class="pending-item">
          <div class="pending-item-header">
            <span class="pending-amount">${expense.currency} ${expense.amount.toFixed(2)}</span>
            <span class="pending-date">${dateStr}</span>
          </div>
          <div class="pending-item-details">
            <span class="pending-category">${expense.category}</span>
            <span class="pending-payment">${expense.payment}</span>
            ${expense.note ? `<span class="pending-note">${expense.note}</span>` : ''}
          </div>
        </div>
      `;
    }).join('');

    this.elements.pendingList.innerHTML = html;
  },

  /**
   * Show auth error modal with re-login option
   */
  async showAuthError() {
    if (!this.elements.authModal) return;

    const count = await getPendingCount(this.db);

    // Update auth modal message
    const messageEl = document.getElementById('auth-error-message');
    if (messageEl) {
      messageEl.textContent = `Your session has expired. ${count} expense${count !== 1 ? 's' : ''} waiting to sync.`;
    }

    this.elements.authModal.classList.remove('hidden');
  },

  /**
   * Close auth error modal
   */
  closeAuthModal() {
    if (!this.elements.authModal) return;
    this.elements.authModal.classList.add('hidden');
  },

  /**
   * Redirect to login page
   */
  goToLogin() {
    window.location.href = '/auth/login';
  }
};

/**
 * Initialize the expense tracker application
 * @param {import('idb').IDBPDatabase} db - Database instance
 */
export async function initApp(db) {
  App.db = db;
  await App.init();
}

/** Export version for external access */
export const APP_VERSION_STRING = APP_VERSION;
